import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  ArrowCounterClockwise, Plus, Trash, Check, CircleNotch, ArrowLeft
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function CreditNoteBuilderPage() {
  const { id: docId } = useParams();
  const isEditMode = !!docId;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [saving, setSaving] = useState(false);
  const [isFirstCreditNote, setIsFirstCreditNote] = useState(false);
  const [startingNumber, setStartingNumber] = useState(1);

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    client_name: '',
    client_address: '',
    client_vat: '',
    client_fiscal_code: '',
    client_email: '',
    reference_document: '',
    reference_date: '',
    reason: '',
    iva_rate: 22,
    notes: ''
  });

  const [items, setItems] = useState([
    { description: '', unit: 'pz', quantity: 1, price: '' }
  ]);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`);
      setClients(Array.isArray(data) ? data : [])
    } catch (err) { console.error(err); }
  }, []);

  const checkFirstCreditNote = useCallback(async () => {
    if (isEditMode) return;
    try {
      const { data } = await axios.get(`${API}/credit-notes`);
      if ((data || []).length === 0) setIsFirstCreditNote(true);
    } catch (err) { console.error(err); }
  }, [isEditMode]);

  const fetchDoc = useCallback(async () => {
    if (!docId) return;
    try {
      const { data } = await axios.get(`${API}/credit-notes/${docId}`);
      setForm({
        date: data.date || '',
        client_name: data.client_name || '',
        client_address: data.client_address || '',
        client_vat: data.client_vat || '',
        client_fiscal_code: data.client_fiscal_code || '',
        client_email: data.client_email || '',
        reference_document: data.reference_document || '',
        reference_date: data.reference_date || '',
        reason: data.reason || '',
        iva_rate: data.iva_rate ?? 22,
        notes: data.notes || ''
      });
      setItems(data.items?.length ? data.items : [{ description: '', unit: 'pz', quantity: 1, price: '' }]);
      setSelectedClientId(data.client_id || '');
    } catch (err) {
      toast.error(t('creditNote.loadError'));
      navigate('/note-credito');
    }
  }, [docId, navigate, t]);

  useEffect(() => { fetchClients(); fetchDoc(); checkFirstCreditNote(); }, [fetchClients, fetchDoc, checkFirstCreditNote]);

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    const client = (clients || []).find(c => c.id === clientId);
    if (client) {
      setForm(f => ({
        ...f,
        client_name: client.name || '',
        client_address: client.address || '',
        client_vat: client.vat_number || '',
        client_fiscal_code: client.fiscal_code || '',
        client_email: client.email || ''
      }));
    }
  };

  const addItem = () => {
    setItems([...items, { description: '', unit: 'pz', quantity: 1, price: '' }]);
  };

  const removeItem = (index) => {
    if ((items || []).length <= 1) return;
    setItems((items || []).filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const subtotal = (items || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0), 0);
  const ivaAmount = subtotal * (parseFloat(form.iva_rate) || 0) / 100;
  const total = subtotal + ivaAmount;

  const handleSave = async () => {
    if (!form.client_name) {
      toast.error(t('creditNote.selectClient'));
      return;
    }
    if ((items || []).length === 0 || !items[0].description) {
      toast.error(t('creditNote.addItem'));
      return;
    }

    setSaving(true);
    const payload = {
      ...form,
      client_id: selectedClientId,
      items: (items || []).filter(i => i.description),
      ...(isFirstCreditNote && startingNumber > 1 ? { starting_number: startingNumber } : {})
    };

    try {
      if (isEditMode) {
        await axios.put(`${API}/credit-notes/${docId}`, payload);
        toast.success(t('creditNote.updated'));
      } else {
        await axios.post(`${API}/credit-notes`, payload);
        toast.success(t('creditNote.created'));
      }
      navigate('/note-credito');
    } catch (err) {
      toast.error(isEditMode ? t('creditNote.updateError') : t('creditNote.createError'));
    }
    setSaving(false);
  };

  const formatCurrency = (val) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/note-credito')} data-testid="back-to-credit-notes">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0A0F1A] flex items-center gap-2">
            <ArrowCounterClockwise size={24} weight="duotone" className="text-[#C53030]" />
            {isEditMode ? t('creditNote.editCreditNote') : t('creditNote.newCreditNote')}
          </h1>
        </div>
      </div>

      {/* Starting number for first credit note */}
      {isFirstCreditNote && !isEditMode && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">{t('creditNote.firstDocNote')}</p>
              <p className="text-xs text-amber-600 mt-1">{t('creditNote.firstDocHint')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-amber-800 whitespace-nowrap">N. partenza:</Label>
              <Input
                type="number" min="1"
                value={startingNumber}
                onChange={e => setStartingNumber(parseInt(e.target.value) || 1)}
                className="w-20 bg-white"
                data-testid="cn-starting-number"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reference & Client */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-[#C53030] uppercase tracking-wider">
            {t('creditNote.section1')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">{t('creditNote.referenceDocument')}</Label>
              <Input
                value={form.reference_document}
                onChange={e => setForm({ ...form, reference_document: e.target.value })}
                className="mt-1"
                placeholder="es. PROF-2026-0001"
                data-testid="cn-reference-doc"
              />
            </div>
            <div>
              <Label className="text-xs">{t('creditNote.referenceDate')}</Label>
              <Input type="date" value={form.reference_date} onChange={e => setForm({ ...form, reference_date: e.target.value })} className="mt-1" data-testid="cn-reference-date" />
            </div>
            <div>
              <Label className="text-xs">{t('creditNote.date')}</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="cn-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t('creditNote.reason')}</Label>
            <Input
              value={form.reason}
              onChange={e => setForm({ ...form, reason: e.target.value })}
              className="mt-1"
              placeholder="es. Errore calcolo, reso merce..."
              data-testid="cn-reason"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t('creditNote.selectClient')}</Label>
              <Select value={selectedClientId} onValueChange={handleClientSelect}>
                <SelectTrigger className="mt-1" data-testid="cn-client-select">
                  <SelectValue placeholder={t('creditNote.selectClientPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {(clients || []).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('creditNote.client')}</Label>
              <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} className="mt-1" data-testid="cn-client-name" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">{t('common.address')}</Label>
              <Input value={form.client_address} onChange={e => setForm({ ...form, client_address: e.target.value })} className="mt-1" data-testid="cn-client-address" />
            </div>
            <div>
              <Label className="text-xs">P.IVA</Label>
              <Input value={form.client_vat} onChange={e => setForm({ ...form, client_vat: e.target.value })} className="mt-1" data-testid="cn-client-vat" />
            </div>
            <div>
              <Label className="text-xs">C.F.</Label>
              <Input value={form.client_fiscal_code} onChange={e => setForm({ ...form, client_fiscal_code: e.target.value })} className="mt-1" data-testid="cn-client-cf" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-bold text-[#C53030] uppercase tracking-wider">
            {t('creditNote.section2')}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={addItem} data-testid="cn-add-item">
            <Plus size={16} className="mr-1" />
            {t('creditNote.addItem')}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {(items || []).map((item, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-[#FFF5F5] rounded-lg" data-testid={`cn-item-${index}`}>
              <span className="text-xs font-bold text-[#C53030] mt-2 w-6 text-center">{index + 1}</span>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-5">
                  <Label className="text-[10px] text-[#4A5568]">{t('creditNote.description')}</Label>
                  <Input
                    value={item.description}
                    onChange={e => updateItem(index, 'description', e.target.value)}
                    className="mt-0.5 bg-white text-sm"
                    placeholder="Descrizione storno..."
                    data-testid={`cn-item-desc-${index}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] text-[#4A5568]">{t('creditNote.unit')}</Label>
                  <Select value={item.unit} onValueChange={v => updateItem(index, 'unit', v)}>
                    <SelectTrigger className="mt-0.5 bg-white text-sm" data-testid={`cn-item-unit-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pz">pz</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="mq">mq</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="nr">nr</SelectItem>
                      <SelectItem value="ore">ore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-[10px] text-[#4A5568]">{t('creditNote.quantity')}</Label>
                  <Input
                    type="number" min="1"
                    value={item.quantity}
                    onChange={e => updateItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                    className="mt-0.5 bg-white text-sm"
                    data-testid={`cn-item-qty-${index}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] text-[#4A5568]">{t('creditNote.unitPrice')}</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={item.price}
                    onChange={e => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                    className="mt-0.5 bg-white text-sm"
                    data-testid={`cn-item-price-${index}`}
                  />
                </div>
                <div className="sm:col-span-1 text-right pt-5">
                  <span className="text-sm font-medium text-[#C53030]">
                    -{formatCurrency((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0))}
                  </span>
                </div>
                <div className="sm:col-span-1 flex items-end">
                  {(items || []).length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeItem(index)} className="text-red-500 hover:bg-red-50 h-9 w-9" data-testid={`cn-item-remove-${index}`}>
                      <Trash size={16} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Totals */}
          <div className="border-t border-[#E2E8F0] pt-4 mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#4A5568]">{t('creditNote.subtotal')}</span>
              <span className="font-medium text-[#C53030]" data-testid="cn-subtotal">-{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-2">
                <span className="text-[#4A5568]">IVA</span>
                <Input
                  type="number" min="0" max="100" step="1"
                  value={form.iva_rate}
                  onChange={e => setForm({ ...form, iva_rate: parseFloat(e.target.value) || 0 })}
                  className="w-16 h-7 text-xs text-center"
                  data-testid="cn-iva-rate"
                />
                <span className="text-[#4A5568]">%</span>
              </div>
              <span className="font-medium text-[#C53030]" data-testid="cn-iva-amount">-{formatCurrency(ivaAmount)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t border-[#C53030] pt-2">
              <span className="text-[#C53030]">{t('creditNote.total')}</span>
              <span className="text-[#C53030]" data-testid="cn-total">-{formatCurrency(total)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-[#C53030] uppercase tracking-wider">
            {t('creditNote.section3')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            rows={3}
            placeholder={t('creditNote.notesPlaceholder')}
            className="border-[#E2E8F0]"
            data-testid="cn-notes"
          />
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/note-credito')} data-testid="cn-cancel">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#C53030] hover:bg-[#9B2C2C] text-white min-w-[180px]"
          data-testid="cn-save"
        >
          {saving ? (
            <><CircleNotch size={18} className="mr-2 animate-spin" /> {isEditMode ? t('creditNote.saving') : t('creditNote.creating')}</>
          ) : (
            <><Check size={18} className="mr-2" /> {isEditMode ? t('creditNote.saveCreditNote') : t('creditNote.createCreditNote')}</>
          )}
        </Button>
      </div>
    </div>
  );
}

